import { useMemo } from 'react';
import { DataTable, StatusBadge, type DataTableColumn } from '@repletoryreach/sdk-ui/data-display';
import { Stack, Text, Badge } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { RateChangeButton, rateMarginText } from './RateVersionModal';
import { CancelUpcomingButton } from './CancelUpcomingButton';
import { PlanPrices, type PlanLabel } from './PlanPrices';
import { Blank } from '../../components/cells';
import { formatDate, formatMinor, formatRelative } from '../../../../lib/format';
import { chargeInForce } from '../../../../lib/priceLadder';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';

type Sku = NonNullable<Awaited<ReturnType<typeof admin.listSkuRates>>['data']>[number];

/**
 * `DataTable` sorts on the record's own fields, so the charge in force is
 * folded into the row: the column sorts on the figure it shows, not on a
 * stored charge the floor may have overtaken.
 */
type Row = Sku & { list: number };

/** Margin below this share of the charge is flagged: the platform is close to selling at cost. */
export const THIN_MARGIN_PERCENT = 10;

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The price book as a table: one row per live rate, the money in right-aligned
 * columns that read left to right as cost, floor, charge, margin, with what
 * each plan pays beside them. The retired versions only join when the operator
 * asks for them, and then a status column says which is which.
 *
 * The charge is the list price in force (what the free plan pays), which is
 * never under the floor, and the margin is the service's own figure on that
 * price. Nothing here is recomputed from the stored charge.
 */
export function SkuRatesTable({ rows, loading, showRetired, plans }: {
  rows: Sku[];
  loading: boolean;
  showRetired: boolean;
  /** The plans on sale, in price-list order, to name what each one pays. */
  plans: PlanLabel[];
}) {
  const t = useT();
  const records = useMemo(() => rows.map((r): Row => ({ ...r, list: chargeInForce(r) })), [rows]);

  const columns: DataTableColumn<Indexed<Row>>[] = [
    {
      accessor: 'name',
      title: t('billing.skuRates.columns.item'),
      sortable: true,
      render: (r) => (
        <TwoLineCell
          primary={r.name}
          secondary={<><code>{r.sku_code}</code> · {t(`billing.skuRates.unit.${r.unit}`, r.unit)}</>}
          title={r.name}
        />
      )
    },
    {
      accessor: 'category',
      title: t('billing.skuRates.columns.category'),
      sortable: true,
      render: (r) => <Badge size="xs" variant="light" color="gray">{t(`billing.skuRates.category.${r.category}`, r.category)}</Badge>
    },
    {
      accessor: 'cost_minor',
      title: t('billing.skuRates.columns.cost'),
      textAlign: 'right',
      sortable: true,
      render: (r) => <Text role="bodySmall" c={MUTED} className="rr-billing-money" fw={500}>{formatMinor(r.cost_minor, r.currency)}</Text>
    },
    {
      accessor: 'floor_minor',
      title: t('billing.skuRates.columns.floor'),
      textAlign: 'right',
      sortable: true,
      // The least the row may be listed at: its cost plus the free plan's markup.
      render: (r) => {
        if (r.floor_minor == null) return <Blank />;
        return (
          <Tooltip label={r.floor_minor > 0 ? t('billing.skuRates.floorHint') : t('billing.skuRates.noFloorHint')} position="top">
            <Text span role="bodySmall" c={MUTED} className="rr-billing-money" fw={500}>
              {r.floor_minor > 0 ? formatMinor(r.floor_minor, r.currency) : t('billing.skuRates.noFloor')}
            </Text>
          </Tooltip>
        );
      }
    },
    {
      accessor: 'list',
      title: t('billing.skuRates.columns.charge'),
      textAlign: 'right',
      sortable: true,
      // A stack rather than `TwoLineCell`, which truncates the second line in a
      // narrow right-aligned column.
      render: (r) => (
        <Stack gap="xs" align="flex-end">
          <Text role="bodySmall" className="rr-billing-money" c={live(r) ? undefined : MUTED}>
            {formatMinor(r.list, r.currency)}
          </Text>
          {r.follows_ladder && (
            <Tooltip label={t('billing.skuRates.ladderBadgeHint')} position="top">
              <Badge size="xs" variant="light" color="blue">{t('billing.skuRates.ladderBadge')}</Badge>
            </Tooltip>
          )}
        </Stack>
      )
    },
    {
      accessor: 'plan_amounts_minor',
      title: t('billing.skuRates.columns.byPlan'),
      render: (r) => <PlanPrices amounts={r.plan_amounts_minor} plans={plans} currency={r.currency} />
    },
    {
      accessor: 'margin_minor',
      title: t('billing.skuRates.columns.margin'),
      textAlign: 'right',
      sortable: true,
      render: (r) => {
        const thin = live(r) && r.list > 0 && (r.margin_percent ?? 0) < THIN_MARGIN_PERCENT;
        return (
          <TwoLineCell
            primary={
              <Text role="bodySmall" fw={600} className="rr-billing-money" c={thin ? 'var(--rr-color-error)' : undefined}>
                {rateMarginText(r)}
              </Text>
            }
            secondary={
              thin
                ? <Badge size="xs" variant="light" color="red">{t('billing.skuRates.thinMargin')}</Badge>
                : r.markup_percent == null ? t('billing.skuRates.noCost') : t('billing.skuRates.markup', { pct: r.markup_percent })
            }
          />
        );
      }
    },
    {
      accessor: 'effective_from',
      title: t('billing.skuRates.columns.effectiveFrom'),
      sortable: true,
      render: (r) => (
        <span title={r.effective_from}>
          <TwoLineCell
            primary={formatDate(r.effective_from)}
            secondary={r.status === 'upcoming'
              ? <Badge size="xs" variant="light" color="blue">{t('billing.skuRates.status.upcoming', { date: formatDate(r.effective_from) })}</Badge>
              : formatRelative(r.effective_from)}
          />
        </span>
      )
    }
  ];

  if (showRetired) {
    columns.push({
      accessor: 'is_active',
      title: t('common.status'),
      render: (r) => <RateStatus rate={r} />
    });
  }

  columns.push({
    accessor: 'actions',
    title: t('common.actions'),
    textAlign: 'right',
    render: (r) => (r.status === 'current' ? <RateChangeButton rate={r} /> : r.status === 'upcoming' ? <CancelUpcomingButton rate={r} /> : null)
  });

  return (
    <DataTable<Indexed<Row>>
      records={records as Indexed<Row>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('billing.skuRates.emptyRows')}
      columns={columns}
    />
  );
}

/** A row that is in force or waiting for its day, as opposed to price book history. */
export const live = (r: Sku) => r.status === 'current' || r.status === 'upcoming';

/** Where a row stands: in force, scheduled, overtaken by a later version, or switched off. */
export function RateStatus({ rate }: { rate: Sku }) {
  const t = useT();
  if (rate.status === 'current') return <StatusBadge status="healthy">{t('status.active')}</StatusBadge>;
  if (rate.status === 'upcoming') {
    return <Badge size="xs" variant="light" color="blue">{t('billing.skuRates.status.upcoming', { date: formatDate(rate.effective_from) })}</Badge>;
  }
  return (
    <StatusBadge status="inactive">
      {rate.status === 'superseded' ? t('billing.skuRates.status.superseded') : t('billing.skuRates.retired')}
    </StatusBadge>
  );
}
